import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Phone, Mic, Calendar, Users, CheckCircle, XCircle, Clock, Search, ChevronRight, ChevronDown, Filter } from 'lucide-react';
import { communicationsApi, BulkCommunicationHistory, MessageLog } from '@/services/communicationsApi';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
type ChannelFilter = 'all' | 'sms' | 'whatsapp' | 'voice' | 'email';

interface CommunicationHistoryPageProps {
  embedded?: boolean;
}

export const CommunicationHistoryPage: React.FC<CommunicationHistoryPageProps> = ({ embedded = false }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [history, setHistory] = useState<BulkCommunicationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | '7days' | '30days' | '90days'>('all');

  // Expanded view
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [messageLogs, setMessageLogs] = useState<Record<string, MessageLog[]>>({});
  const [loadingLogs, setLoadingLogs] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await communicationsApi.getBulkCommunicationHistory({
        organization_id: 1,
        limit: 100,
      });
      setHistory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load communication history');
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMessageLogs = async (bulkCommId: string) => {
    if (messageLogs[bulkCommId]) {
      // Already loaded
      return;
    }

    try {
      setLoadingLogs({ ...loadingLogs, [bulkCommId]: true });
      const logs = await communicationsApi.getMessageLogs(bulkCommId, {
        limit: 50,
      });
      setMessageLogs({ ...messageLogs, [bulkCommId]: logs });
    } catch (err) {
      console.error('Failed to load message logs:', err);
    } finally {
      setLoadingLogs({ ...loadingLogs, [bulkCommId]: false });
    }
  };

  const handleExpandToggle = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      loadMessageLogs(id);
    }
  };

  // Filter history
  const filteredHistory = history.filter((item) => {
    // Channel filter
    if (channelFilter !== 'all' && item.channel !== channelFilter) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !item.group_name.toLowerCase().includes(query) &&
        !item.message_template.toLowerCase().includes(query) &&
        !item.created_by_name.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    // Date filter
    if (dateFilter !== 'all') {
      const createdDate = new Date(item.created_at);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

      if (dateFilter === '7days' && daysDiff > 7) return false;
      if (dateFilter === '30days' && daysDiff > 30) return false;
      if (dateFilter === '90days' && daysDiff > 90) return false;
    }

    return true;
  });

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'sms':
        return <MessageSquare className="w-5 h-5 text-blue-600" />;
      case 'whatsapp':
        return <Phone className="w-5 h-5 text-green-600" />;
      case 'voice':
        return <Mic className="w-5 h-5 text-indigo-600" />;
      case 'email':
        return <Mail className="w-5 h-5 text-purple-600" />;
      default:
        return <MessageSquare className="w-5 h-5 text-gray-600" />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'sms':
        return 'bg-blue-100 text-blue-800';
      case 'whatsapp':
        return 'bg-green-100 text-green-800';
      case 'voice':
        return 'bg-indigo-100 text-indigo-800';
      case 'email':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-700">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">{t('communications.loading_communication_history', 'Loading communication history...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'h-full'} flex flex-col bg-white dark:bg-gray-900`}>
      {/* Description — hidden when embedded in analytics */}
      {!embedded && (
      <div className="bg-surface border-b border-v2border px-6 py-4">
        <p className="text-[13px] text-ink-500">
          {t('communications.view_and_track_all_bulk', 'View and track all bulk communications sent to member groups')}
        </p>
      </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('communications.search_by_group_message_or', 'Search by group, message, or sender...')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Channel Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value as ChannelFilter)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('communications.all_channels', 'All Channels')}</option>
              <option value="email">{t('communications.email', 'Email')}</option>
              <option value="sms">SMS</option>
              <option value="whatsapp">{t('communications.whatsapp', 'WhatsApp')}</option>
              <option value="voice">{t('communications.voice_ai', 'Voice AI')}</option>
            </select>
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-600" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">{t('communications.all_time', 'All Time')}</option>
              <option value="7days">{t('communications.last_7_days', 'Last 7 Days')}</option>
              <option value="30days">{t('communications.last_30_days', 'Last 30 Days')}</option>
              <option value="90days">{t('communications.last_90_days', 'Last 90 Days')}</option>
            </select>
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {filteredHistory.length} {filteredHistory.length === 1 ? 'result' : 'results'}
          </div>
        </div>
      </div>

      {/* History List */}
      <div className="flex-1 overflow-y-auto p-6">
        {error && (
          <div className="max-w-6xl mx-auto mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <XCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {filteredHistory.length === 0 ? (
          <div className="max-w-6xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('communications.no_communications_found', 'No communications found')}</h3>
            <p className="text-gray-600 dark:text-gray-300">
              {searchQuery || channelFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Start sending bulk messages to see them here'}
            </p>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-4">
            {filteredHistory.map((item) => (
              <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Communication Summary */}
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Channel Icon */}
                      <div className="mt-1">{getChannelIcon(item.channel)}</div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.group_name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getChannelColor(item.channel)}`}>
                            {item.channel.toUpperCase()}
                          </span>
                        </div>

                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.message_template}</p>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(item.created_at)}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {item.total_members} members
                          </div>
                          <div>by {item.created_by_name}</div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item.status)}
                        <span className="text-sm font-medium text-gray-700 capitalize">{item.status}</span>
                      </div>
                    </div>

                    {/* Expand Button */}
                    <button
                      onClick={() => handleExpandToggle(item.id)}
                      className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {expandedId === item.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-green-700">{t('communications.sent', 'Sent')}</span>
                      </div>
                      <div className="text-xl font-bold text-green-900">{item.sent_count}</div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-xs font-medium text-red-700">{t('communications.failed', 'Failed')}</span>
                      </div>
                      <div className="text-xl font-bold text-red-900">{item.failed_count}</div>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium text-blue-700">{t('communications.success_rate', 'Success Rate')}</span>
                      </div>
                      <div className="text-xl font-bold text-blue-900">
                        {item.total_members > 0
                          ? Math.round((item.sent_count / item.total_members) * 100)
                          : 0}
                        %
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Message Logs */}
                {expandedId === item.id && (
                  <div className="border-t border-gray-200 bg-gray-50 dark:bg-gray-700 p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">{t('communications.individual_message_logs', 'Individual Message Logs')}</h4>

                    {loadingLogs[item.id] ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{t('communications.loading_message_logs', 'Loading message logs...')}</p>
                      </div>
                    ) : messageLogs[item.id] && messageLogs[item.id].length > 0 ? (
                      <div className="space-y-2">
                        {messageLogs[item.id].map((log) => (
                          <div key={log.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {log.email || log.phone_number}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      log.status === 'delivered'
                                        ? 'bg-green-100 text-green-800'
                                        : log.status === 'failed'
                                        ? 'bg-red-100 text-red-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                  >
                                    {log.status}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 line-clamp-1">{log.message_content}</p>
                                {log.error_message && (
                                  <p className="text-xs text-red-600 mt-1">{log.error_message}</p>
                                )}
                              </div>
                              <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                                {log.sent_at && formatDate(log.sent_at)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-600 dark:text-gray-300">
                        <p className="text-sm">{t('communications.no_message_logs_available', 'No message logs available')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
